import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  getRequestDetail,
  type RequestDetail,
  type RequestOperationSnapshot,
  type RequestSummary,
} from "../api/requests";
import { useRequestSSE } from "./useRequestSSE";

export type WatchedRequestStatus = "loading" | "ready" | "missing";

export interface UseWatchedRequestResult {
  detail: RequestDetail | null;
  status: WatchedRequestStatus;
}

const DETAIL_REFRESH_FIELDS = [
  "status",
  "operationId",
  "fundedAt",
  "acceptedAt",
] as const;

function detailToSummary(detail: RequestDetail): RequestSummary {
  return {
    requestId: detail.requestId,
    status: detail.status,
    amount: detail.amount,
    corridor: detail.corridor,
    fundRequestUrl: detail.fundRequestUrl,
    fundRequestLinkId: detail.fundRequestLinkId,
    operationId: detail.operationId,
    acceptedAt: detail.acceptedAt,
    fundedAt: detail.fundedAt,
    expiresAt: detail.expiresAt,
    createdAt: detail.createdAt,
    payerName: detail.payerName,
    operation: detail.operation
      ? {
          operationId: detail.operation.operationId,
          status: detail.operation.status,
          fundingMethod: detail.operation.fundingMethod,
          fundingStatus: detail.operation.fundingStatus,
        }
      : undefined,
  };
}

function shouldRefreshDetail(
  previous: RequestSummary | null,
  updated: RequestSummary
): boolean {
  if (!previous) return true;
  return DETAIL_REFRESH_FIELDS.some(
    (field) => previous[field] !== updated[field]
  );
}

function operationSnapshotChanged(
  previous: RequestSummary | null,
  updated: RequestSummary
): boolean {
  if (!updated.operation) return false;
  const prev = previous?.operation;
  if (!prev) return true;
  return (
    prev.status !== updated.operation.status ||
    prev.fundingStatus !== updated.operation.fundingStatus ||
    prev.fundingMethod !== updated.operation.fundingMethod
  );
}

function mergeSummaryIntoDetail(
  detail: RequestDetail,
  summary: RequestSummary
): RequestDetail {
  return {
    ...detail,
    status: summary.status,
    amount: summary.amount,
    corridor: summary.corridor,
    fundRequestUrl: summary.fundRequestUrl,
    fundRequestLinkId: summary.fundRequestLinkId,
    operationId: summary.operationId,
    acceptedAt: summary.acceptedAt,
    fundedAt: summary.fundedAt,
    expiresAt: summary.expiresAt,
    createdAt: summary.createdAt,
    payerName: summary.payerName ?? detail.payerName,
    operation: summary.operation
      ? mergeOperationSnapshot(detail.operation, summary.operation)
      : detail.operation,
  };
}

function mergeOperationSnapshot(
  operation: RequestDetail["operation"] | null | undefined,
  snapshot: RequestOperationSnapshot
): RequestDetail["operation"] | null {
  if (!operation) {
    // Caller must refreshDetail when detail has no operation yet — a slim
    // snapshot cannot fill economics fields on the receipt.
    return null;
  }
  return {
    ...operation,
    operationId: snapshot.operationId,
    status: snapshot.status,
    fundingMethod: snapshot.fundingMethod ?? operation.fundingMethod,
    fundingStatus: snapshot.fundingStatus ?? operation.fundingStatus,
  };
}

/**
 * Loads and live-tracks a single money request for the receipt screen.
 *
 * SSE payloads are `RequestSummary` with an optional operation snapshot; we
 * merge lightweight operation deltas locally and re-fetch full detail when
 * fulfillment-related request fields change.
 */
export function useWatchedRequest(
  linkId: string | null | undefined,
  requestId: string | null | undefined
): UseWatchedRequestResult {
  const [detail, setDetail] = useState<RequestDetail | null>(null);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const summaryRef = useRef<RequestSummary | null>(null);

  const watchedRequests = useMemo(
    () => (detail ? [detailToSummary(detail)] : []),
    [detail]
  );

  const refreshDetail = useCallback(async () => {
    if (!linkId || !requestId) return;
    try {
      const next = await getRequestDetail(linkId, requestId);
      summaryRef.current = detailToSummary(next);
      setDetail(next);
    } catch (error) {
      console.error("[useWatchedRequest] Failed to refresh request detail:", error);
    }
  }, [linkId, requestId]);

  const handleUpsert = useCallback(
    (summary: RequestSummary) => {
      if (summary.requestId !== requestId) return;

      if (shouldRefreshDetail(summaryRef.current, summary)) {
        summaryRef.current = summary;
        void refreshDetail();
        return;
      }

      if (operationSnapshotChanged(summaryRef.current, summary)) {
        summaryRef.current = summary;
        setDetail((prev) => {
          if (!prev) {
            void refreshDetail();
            return prev;
          }
          if (!summary.operation) return prev;
          // Detail loaded before the fulfilling op existed — need full receipt.
          if (!prev.operation) {
            void refreshDetail();
            return prev;
          }
          const mergedOperation = mergeOperationSnapshot(
            prev.operation,
            summary.operation
          );
          if (!mergedOperation) {
            void refreshDetail();
            return prev;
          }
          return { ...prev, operation: mergedOperation };
        });
        return;
      }

      summaryRef.current = summary;
      setDetail((prev) => {
        if (!prev) {
          void refreshDetail();
          return prev;
        }
        const merged = mergeSummaryIntoDetail(prev, summary);
        if (summary.operation && !merged.operation) {
          void refreshDetail();
        }
        return merged;
      });
    },
    [requestId, refreshDetail]
  );

  useRequestSSE(
    linkId,
    watchedRequests,
    handleUpsert,
    refreshDetail,
    { watchRequestId: requestId ?? undefined }
  );

  useEffect(() => {
    if (!linkId || !requestId) {
      setDetail(null);
      summaryRef.current = null;
      setInitialLoadDone(false);
      return;
    }

    let cancelled = false;
    setInitialLoadDone(false);
    setDetail(null);
    summaryRef.current = null;

    getRequestDetail(linkId, requestId)
      .then((data) => {
        if (cancelled) return;
        summaryRef.current = detailToSummary(data);
        setDetail(data);
      })
      .catch((error) => {
        console.error("[useWatchedRequest] Failed to fetch request:", error);
      })
      .finally(() => {
        if (!cancelled) setInitialLoadDone(true);
      });

    return () => {
      cancelled = true;
    };
  }, [linkId, requestId]);

  let status: WatchedRequestStatus;
  if (detail) {
    status = "ready";
  } else if (!initialLoadDone) {
    status = "loading";
  } else {
    status = "missing";
  }

  return { detail, status };
}
